import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { EmptyState } from "@/components/app/lists/empty-state";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useViewingConversation } from "@/lib/messaging/notices/notify";
import { conversationQuery } from "@/lib/messaging/live/queries";
import { ConversationView } from "../-components/conversation/conversation-view";

export const Route = createFileRoute("/_authenticated/messaging/_inbox/$conversationId")({
  validateSearch: (search: Record<string, unknown>): { thread?: string; message?: string } => ({
    thread: typeof search.thread === "string" ? search.thread : undefined,
    message: typeof search.message === "string" ? search.message : undefined,
  }),
  // Both reads start the moment the conversation is hovered or chosen, side by side; history's
  // code is fetched lazily so it stays out of the route tree every page loads.
  loader: ({ context, params }) => {
    void context.queryClient.prefetchQuery(conversationQuery(params.conversationId));
    void import("@/lib/messaging/history/history")
      .then(({ prefetchHistory }) => prefetchHistory(context.queryClient, params.conversationId))
      .catch(() => undefined);
  },
  component: ConversationPage,
});

function ConversationPage() {
  const { conversationId } = Route.useParams();
  const detail = useQuery(conversationQuery(conversationId));
  useViewingConversation(conversationId);
  if (detail.isPending) {
    return (
      <div className="space-y-3 p-4" aria-busy>
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (detail.isError) {
    const missing = (detail.error as { code?: string }).code === "NOT_FOUND";
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        {missing ? (
          <EmptyState
            title="This conversation is not available"
            description="You may have left it, been taken out of it, or it belongs to a building you no longer work at."
            action={
              <Button asChild variant="outline">
                <Link to="/messaging">All conversations</Link>
              </Button>
            }
          />
        ) : (
          <ReadFailed
            message="Could not open the conversation."
            error={detail.error}
            onRetry={() => void detail.refetch()}
          />
        )}
      </div>
    );
  }
  return <ConversationView key={conversationId} detail={detail.data} />;
}
